import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../../../lib/api";
import { toast } from "sonner";
import { Activity, AlertTriangle, Calendar, Clock, DollarSign, ShieldCheck, Users } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import SearchAndFilter from "../../../../components/SearchAndFilter";
import CarrierClaimPill from "../../../dropship/CarrierClaimPill";
import { ClaimEvidenceGallery } from "../../../dropship/FileClaimDialog";
import VerqoShippingClaimTimeline from "../../../dropship/VerqoShippingClaimTimeline";
import { EmptyState, MetricCard, SectionHeading } from "../shared";

/**
 * ClaimsTab — the seller's claims & carrier-recovery view. Filters,
 * search, metric cards, accept/waive/contest actions and the
 * evidence-gallery confirm dialog.
 * Extracted from SellerDropship.jsx in Phase 7 refactor.
 */
export default function ClaimsTab({ reload }) {
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [statusChip, setStatusChip] = useState("all");
  const [confirm, setConfirm] = useState(null); // { claim, action } OR { bulk: true, action, ids }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = () => api.get("/dropship/claims").then(r => setClaims(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const submitBulk = async () => {
    if (!confirm || !confirm.bulk) return;
    setSubmitting(true);
    try {
      const r = await api.post("/dropship/claims/bulk-action", {
        claim_ids: Array.from(confirm.ids),
        action: confirm.action,
        note: note.trim() || null,
      });
      const { succeeded, failed } = r.data;
      if (failed.length === 0) toast.success(`${succeeded} claim${succeeded === 1 ? "" : "s"} ${confirm.action}d.`);
      else toast.warning(`${succeeded} ${confirm.action}d, ${failed.length} failed.`);
      setConfirm(null); setNote(""); clearSelection();
      load(); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally { setSubmitting(false); }
  };

  const submitAction = async () => {
    if (!confirm) return;
    setSubmitting(true);
    const status = confirm.action === "waive"   ? "waived"
                 : confirm.action === "contest" ? "contested"
                 : confirm.action === "accept"  ? "accepted"
                 : null;
    if (!status) { setSubmitting(false); return; }
    try {
      await api.patch(`/dropship/claims/${confirm.claim.id}`, { status, note: note.trim() || null });
      const msg = {
        waive:   `Waived — buyer will pay the full invoice (${money(confirm.claim.deduction)} absorbed).`,
        contest: "Contested — buyer has been notified. 30-day timer started.",
        accept:  `Accepted — ${money(confirm.claim.deduction)} will appear on the next invoice as a prior-period adjustment.`,
      }[confirm.action];
      toast.success(msg);
      setConfirm(null); setNote("");
      load(); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const kindOptions = useMemo(() => {
    const seen = new Set();
    claims.forEach(c => { if (c.kind) seen.add(c.kind); });
    return [...seen].map(v => ({ value: v, label: v.replace("_", " ") }));
  }, [claims]);
  const buyerOptions = useMemo(() => {
    const seen = new Set();
    claims.forEach(c => { if (c.buyer_name) seen.add(c.buyer_name); });
    return [...seen].map(v => ({ value: v, label: v }));
  }, [claims]);

  // Normalize legacy 'disputed' to 'contested' for display/filtering parity.
  const normStatus = (s) => s === "disputed" ? "contested" : s;

  const filtered = useMemo(() => {
    let xs = claims;
    if (statusChip !== "all") xs = xs.filter(c => normStatus(c.status) === statusChip);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(c =>
        (c.order_code || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q) ||
        (c.kind || "").toLowerCase().includes(q) ||
        (c.buyer_name || "").toLowerCase().includes(q)
      );
    }
    const F = filters || {};
    if (F.kind?.values?.length)   xs = xs.filter(c => F.kind.values.includes(c.kind));
    if (F.status?.values?.length) xs = xs.filter(c => F.status.values.includes(normStatus(c.status)));
    if (F.buyer?.values?.length)  xs = xs.filter(c => F.buyer.values.includes(c.buyer_name));
    if (F.deduction?.value != null) {
      xs = xs.filter(c => {
        const v = Number(c.deduction) || 0;
        if (F.deduction.op === "gt") return v > Number(F.deduction.value);
        if (F.deduction.op === "lt") return v < Number(F.deduction.value);
        if (F.deduction.op === "between") return v >= Number(F.deduction.min) && v <= Number(F.deduction.max);
        return true;
      });
    }
    if (F.orderValue?.value != null) {
      xs = xs.filter(c => {
        const v = Number(c.order_value) || 0;
        if (F.orderValue.op === "gt") return v > Number(F.orderValue.value);
        if (F.orderValue.op === "lt") return v < Number(F.orderValue.value);
        if (F.orderValue.op === "between") return v >= Number(F.orderValue.min) && v <= Number(F.orderValue.max);
        return true;
      });
    }
    if (F.date?.value) xs = xs.filter(c => (c.created_at || "") >= F.date.value);
    return xs;
  }, [claims, statusChip, search, filters]);

  const chipCount = (s) => claims.filter(c => normStatus(c.status) === s).length;

  // Financial summary strip
  const summary = useMemo(() => {
    const pend = claims.filter(c => c.status === "pending");
    const waived = claims.filter(c => c.status === "waived");
    const applied = claims.filter(c => c.status === "applied");
    return {
      pendingDeduction: pend.reduce((a, c) => a + (Number(c.deduction) || 0), 0),
      waivedAbsorbed: waived.reduce((a, c) => a + (Number(c.original_deduction ?? c.deduction) || 0), 0),
      appliedDeducted: applied.reduce((a, c) => a + (Number(c.deduction) || 0), 0),
    };
  }, [claims]);

  return (
    <div className="space-y-5">
      <SectionHeading title="Claims & deductions"
                       sub="Buyers file claims when terms are breached. Pending claims auto-deduct from the next daily invoice — no action needed if the rule is correct. You can waive (absorb the cost as goodwill, buyer pays full invoice) or contest (open negotiation with the buyer) before it's applied."/>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="Pending auto-deduction" value={money(summary.pendingDeduction)} tone="amber" icon={Clock}/>
        <MetricCard label="You've absorbed (waived)" value={money(summary.waivedAbsorbed)} tone="emerald" icon={ShieldCheck}/>
        <MetricCard label="Deducted this period" value={money(summary.appliedDeducted)} tone="rose" icon={AlertTriangle}/>
      </div>

      {/* Quick chips */}
      <div className="flex gap-1.5 flex-wrap" data-testid="claims-filter-chips">
        {[
          { k: "all",       l: "All",       c: claims.length },
          { k: "pending",   l: "Pending",   c: chipCount("pending") },
          { k: "waived",    l: "Waived",    c: chipCount("waived") },
          { k: "contested", l: "Contested", c: chipCount("contested") },
          { k: "accepted",  l: "Accepted",  c: chipCount("accepted") },
          { k: "applied",   l: "Applied",   c: chipCount("applied") },
        ].map(f => (
          <button key={f.k} onClick={() => setStatusChip(f.k)} data-testid={`claims-filter-${f.k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[12.5px] border transition-colors ${statusChip === f.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>
            {f.l}
            <span className={`text-[10.5px] mono px-1.5 rounded-full ${statusChip === f.k ? "bg-background/20" : "bg-secondary"}`}>{f.c}</span>
          </button>
        ))}
      </div>

      {/* Power-user facet bar */}
      <SearchAndFilter
        page="seller_dropship_claims"
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={setFilters}
        totalCount={filtered.length}
        facets={[
          { id: "kind",       label: "Kind",       icon: AlertTriangle, type: "multi", options: kindOptions },
          { id: "status",     label: "Status",     icon: Activity,      type: "multi", options: [
            { value: "pending",   label: "Pending" },
            { value: "waived",    label: "Waived" },
            { value: "contested", label: "Contested" },
            { value: "accepted",  label: "Accepted" },
            { value: "applied",   label: "Applied" },
          ]},
          { id: "buyer",      label: "Buyer",      icon: Users,         type: "multi", options: buyerOptions },
          { id: "deduction",  label: "Deduction",  icon: DollarSign,    type: "numeric", numericLabel: "$" },
          { id: "orderValue", label: "Order value",icon: DollarSign,    type: "numeric", numericLabel: "$" },
          { id: "date",       label: "Filed after",icon: Calendar,      type: "date" },
        ]}
      />

      {/* Bulk action bar — shows when ≥1 row selected */}
      {selected.size > 0 && (() => {
        const selectedRows = claims.filter(c => selected.has(c.id));
        const allPending   = selectedRows.every(c => c.status === "pending");
        const allActionable = selectedRows.every(c => c.status === "pending" || c.status === "contested");
        const canWaive   = allActionable;   // waive is valid from pending or contested
        const canContest = allPending;       // contest only makes sense for pending rows
        const canAccept  = allActionable;    // accept works for pending or contested
        const mixedHint = !allActionable
          ? "Some selected rows are already closed — can't act in bulk."
          : !allPending
          ? "Some selected rows are contested — contest is only valid for all-pending."
          : null;
        return (
          <div className="sticky top-3 z-20 rounded-2xl border border-foreground/20 bg-foreground text-background shadow-lg px-5 py-3 flex items-center gap-4 flex-wrap" data-testid="claims-bulk-bar">
            <div className="text-[13px] font-medium">{selected.size} selected</div>
            {mixedHint && <div className="text-[11.5px] text-background/70 hidden md:block">{mixedHint}</div>}
            <div className="ml-auto flex items-center gap-2">
              <button
                disabled={!canWaive}
                onClick={() => { setConfirm({ bulk: true, action: "waive", ids: new Set(selected), rows: selectedRows }); setNote(""); }}
                data-testid="bulk-waive-btn"
                className={`rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] transition-colors ${canWaive ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white/10 text-background/40 cursor-not-allowed"}`}>
                Waive
              </button>
              <button
                disabled={!canAccept}
                onClick={() => { setConfirm({ bulk: true, action: "accept", ids: new Set(selected), rows: selectedRows }); setNote(""); }}
                data-testid="bulk-accept-btn"
                className={`rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] transition-colors ${canAccept ? "bg-indigo-500 text-white hover:bg-indigo-600" : "bg-white/10 text-background/40 cursor-not-allowed"}`}>
                Accept
              </button>
              <button
                disabled={!canContest}
                onClick={() => { setConfirm({ bulk: true, action: "contest", ids: new Set(selected), rows: selectedRows }); setNote(""); }}
                data-testid="bulk-contest-btn"
                className={`rounded-full h-9 px-4 text-[12.5px] mono uppercase tracking-[0.14em] transition-colors ${canContest ? "bg-background/10 text-background border border-background/30 hover:bg-background/20" : "bg-white/10 text-background/40 cursor-not-allowed border border-background/10"}`}>
                Contest
              </button>
              <button onClick={clearSelection} data-testid="bulk-clear-btn"
                className="text-[11.5px] mono uppercase tracking-[0.14em] text-background/70 hover:text-background ml-2">
                Clear
              </button>
            </div>
          </div>
        );
      })()}

      {filtered.length === 0 ? (
        <EmptyState icon={ShieldCheck} title={claims.length === 0 ? "No claims filed" : "No claims match these filters"} body={claims.length === 0 ? "Once a buyer files a claim against an order, it appears here. Pending claims auto-apply to the next daily invoice." : "Clear a filter or chip above to see more claims."}/>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          {(() => {
            const visibleSelectable = filtered.filter(c => c.status === "pending" || c.status === "contested");
            const allVisibleSelected = visibleSelectable.length > 0 && visibleSelectable.every(c => selected.has(c.id));
            const someVisibleSelected = visibleSelectable.some(c => selected.has(c.id));
            const toggleAll = () => {
              setSelected(s => {
                const n = new Set(s);
                if (allVisibleSelected) visibleSelectable.forEach(c => n.delete(c.id));
                else visibleSelectable.forEach(c => n.add(c.id));
                return n;
              });
            };
            return (
              <div className="grid grid-cols-[36px_110px_140px_1fr_100px_110px_110px_180px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
                <div className="flex items-center justify-center">
                  <input type="checkbox"
                    data-testid="bulk-select-all"
                    disabled={visibleSelectable.length === 0}
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5 rounded border-foreground/30 cursor-pointer disabled:cursor-not-allowed"/>
                </div>
                <div>Order</div><div>Kind</div><div>Description</div><div className="text-right">Order val</div><div className="text-right">Deduction</div><div className="text-center">Status</div><div className="text-right">Action</div>
              </div>
            );
          })()}
          <div className="divide-y divide-border">
            {filtered.map(c => {
              const s = normStatus(c.status);
              const isSellerTurn = s === "contested" && (c.last_action_by === "buyer" || c.contested_by === "buyer");
              const sellerWaitingOnBuyer = s === "contested" && (c.last_action_by === "seller" || c.contested_by === "seller") && !isSellerTurn;
              const daysLeft = (() => {
                const iso = c.resolution_deadline;
                if (!iso) return null;
                const d = new Date(iso).getTime() - Date.now();
                return Number.isNaN(d) ? null : Math.max(0, Math.ceil(d / 86400000));
              })();
              const rowSelectable = c.status === "pending" || c.status === "contested";
              const rowSelected = selected.has(c.id);
              return (
                <div key={c.id} className={`grid grid-cols-[36px_110px_140px_1fr_100px_110px_110px_180px] gap-3 px-5 py-3 items-center text-[13px] transition-colors ${rowSelected ? "bg-foreground/[0.04]" : ""}`} data-testid={`claim-row-${c.id}`}>
                  <div className="flex items-center justify-center">
                    <input type="checkbox"
                      data-testid={`row-check-${c.id}`}
                      disabled={!rowSelectable}
                      checked={rowSelected}
                      onChange={() => toggleOne(c.id)}
                      className="h-3.5 w-3.5 rounded border-foreground/30 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"/>
                  </div>
                  <div className="mono text-[12px]">{c.order_code}</div>
                  <div className="capitalize">
                    {c.kind.replace("_", " ")}
                    {c.carrier_claim && (
                      <CarrierClaimPill cc={c.carrier_claim}/>
                    )}
                  </div>
                  <div className="truncate text-foreground/70">{c.description}</div>
                  <div className="text-right">{money(c.order_value)}</div>
                  <div className="text-right font-semibold">
                    {s === "waived"
                      ? <span className="text-emerald-700">Waived <span className="text-[10px] text-foreground/45 mono">(was {money(c.original_deduction ?? 0)})</span></span>
                      : s === "accepted"
                      ? <span className="text-indigo-700">{money(c.deduction)}</span>
                      : <span className="text-rose-700">− {money(c.deduction)}</span>}
                  </div>
                  <div className="flex flex-col items-center">
                    <span className={`text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                      s === "pending"   ? "bg-amber-50 text-amber-700 border-amber-200" :
                      s === "waived"    ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      s === "contested" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                      s === "accepted"  ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                      s === "applied"   ? "bg-rose-50 text-rose-700 border-rose-200" :
                      "bg-secondary text-foreground/55 border-border"
                    }`}>{s}</span>
                    {s === "contested" && daysLeft != null && (
                      <div className="text-[9.5px] text-foreground/50 mt-0.5">
                        {isSellerTurn ? `${daysLeft}d to reply` : `${daysLeft}d for buyer`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    {s === "pending" ? (
                      <>
                        <button
                          onClick={() => { setConfirm({ claim: c, action: "waive" }); setNote(""); }}
                          data-testid={`waive-${c.id}`}
                          title="Absorb this claim. Deduction becomes $0 and the buyer pays the full invoice. Logged as goodwill."
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors">
                          Waive
                        </button>
                        <button
                          onClick={() => { setConfirm({ claim: c, action: "contest" }); setNote(""); }}
                          data-testid={`contest-${c.id}`}
                          title="Disagree with this claim. Opens a negotiation — the buyer will be notified and auto-deduction is paused."
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border text-foreground/75 hover:bg-secondary transition-colors">
                          Contest
                        </button>
                      </>
                    ) : isSellerTurn ? (
                      <>
                        <button
                          onClick={() => { setConfirm({ claim: c, action: "accept" }); setNote(""); }}
                          data-testid={`accept-${c.id}`}
                          title="Accept the buyer's claim. Rolls onto the next invoice as a prior-period adjustment."
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-colors">
                          Accept
                        </button>
                        <button
                          onClick={() => { setConfirm({ claim: c, action: "contest" }); setNote(""); }}
                          data-testid={`re-contest-${c.id}`}
                          title="Re-contest. Resets the 30-day timer; if you and the buyer don't resolve outside the system, the buyer's push-back will auto-apply."
                          className="text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full border border-border text-foreground/75 hover:bg-secondary transition-colors">
                          Re-contest
                        </button>
                      </>
                    ) : sellerWaitingOnBuyer ? (
                      <span className="text-[10.5px] mono text-foreground/50">Awaiting buyer</span>
                    ) : (
                      <span className="text-[10.5px] mono text-foreground/40">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirm dialog (Waive / Contest / Accept — single OR bulk) */}
      <Dialog open={!!confirm} onOpenChange={(v) => { if (!v) { setConfirm(null); setNote(""); } }}>
        <DialogContent className="max-w-md" data-testid="claim-action-dialog">
          {confirm && (() => {
            const isBulk = !!confirm.bulk;
            const count = isBulk ? confirm.ids.size : 1;
            const totalDeduction = isBulk
              ? (confirm.rows || []).reduce((a, c) => a + (Number(c.deduction) || 0), 0)
              : Number(confirm.claim?.deduction || 0);
            const action = confirm.action;
            const titleSingular = { waive: "Waive this claim?", accept: "Accept this claim?", contest: "Contest this claim?" }[action];
            const titleBulk     = { waive: `Waive ${count} claims?`, accept: `Accept ${count} claims?`, contest: `Contest ${count} claims?` }[action];
            return (
            <>
              <DialogHeader>
                <DialogTitle>{isBulk ? titleBulk : titleSingular}</DialogTitle>
              </DialogHeader>

              <div className="rounded-xl bg-secondary/40 border border-border p-3 text-[13px] space-y-1">
                {isBulk ? (
                  <>
                    <div><span className="text-foreground/55">Claims selected:</span> <span className="font-semibold">{count}</span></div>
                    <div><span className="text-foreground/55">Combined deduction at stake:</span> <span className="font-semibold">{money(totalDeduction)}</span></div>
                  </>
                ) : (
                  <>
                    <div><span className="text-foreground/55">Order:</span> <span className="mono">{confirm.claim.order_code}</span></div>
                    <div><span className="text-foreground/55">Kind:</span> <span className="capitalize">{(confirm.claim.kind || "").replace("_", " ")}</span></div>
                    <div><span className="text-foreground/55">Deduction at stake:</span> <span className="font-semibold">{money(confirm.claim.deduction)}</span></div>
                  </>
                )}
              </div>

              {/* Buyer-supplied evidence photos — critical for damaged claims */}
              {!isBulk && (confirm.claim.evidence_photo_count || 0) > 0 && (
                <ClaimEvidenceGallery claim={confirm.claim}/>
              )}

              {/* Iter 238 · Live Verqo Shipping (EasyPost) claim status */}
              {!isBulk && confirm.claim?.easypost_claim_id && (
                <VerqoShippingClaimTimeline claimId={confirm.claim.id} role="seller"/>
              )}

              <div className={`rounded-xl border p-3 text-[12.5px] ${
                action === "waive"  ? "bg-emerald-50 border-emerald-200" :
                action === "accept" ? "bg-indigo-50 border-indigo-200" :
                "bg-amber-50 border-amber-200"}`}>
                {action === "waive" && (
                  <>
                    <div className="font-semibold text-emerald-800">You'll absorb {money(totalDeduction)} as goodwill{isBulk ? ` across ${count} claims` : ""}.</div>
                    <div className="text-emerald-900/75 mt-0.5">Deductions become $0. Buyer{isBulk ? "s" : ""} pay the full invoice. Logged as goodwill.</div>
                  </>
                )}
                {action === "accept" && (
                  <>
                    <div className="font-semibold text-indigo-800">You'll accept {money(totalDeduction)} as deduction{isBulk ? "s" : ""}.</div>
                    <div className="text-indigo-900/75 mt-0.5">Each will roll onto the <strong>next</strong> daily invoice as a "prior-period adjustment". Buyer{isBulk ? "s" : ""} will be notified.</div>
                  </>
                )}
                {action === "contest" && (
                  <>
                    <div className="font-semibold text-amber-800">Contest opens a 30-day window{isBulk ? ` on ${count} claims` : ""}.</div>
                    <div className="text-amber-900/75 mt-0.5">
                      {isBulk ? "Each buyer is" : "The buyer is"} notified and has 30 days to push back or accept your position.
                      If silent, the claim auto-closes in your favor. If pushed back, you'll have 30 days to respond before the deduction auto-applies.
                    </div>
                  </>
                )}
              </div>

              <div>
                <Label className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">{isBulk ? "Note applied to all (optional)" : "Internal note (optional)"}</Label>
                <Input value={note} onChange={e => setNote(e.target.value)}
                  placeholder={action === "waive" ? "e.g. Carrier's fault — waiving for all affected"
                    : action === "accept" ? "e.g. Warehouse mix-up — accepting the lot"
                    : "e.g. Photos show undamaged products"}
                  className="mt-1 h-10" data-testid="claim-action-note"/>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setConfirm(null); setNote(""); }} className="rounded-full h-10 px-4">Cancel</Button>
                <Button onClick={isBulk ? submitBulk : submitAction} disabled={submitting}
                  data-testid="claim-action-confirm"
                  className={`rounded-full h-10 px-4 ${
                    action === "waive"  ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
                    action === "accept" ? "bg-indigo-600 hover:bg-indigo-700 text-white" :
                    "bg-foreground text-background hover:bg-foreground/90"
                  }`}>
                  {submitting ? "…" : action === "waive" ? (isBulk ? `Waive ${count}` : "Confirm waive")
                    : action === "accept" ? (isBulk ? `Accept ${count}` : "Confirm accept")
                    : (isBulk ? `Contest ${count}` : "Confirm contest")}
                </Button>
              </div>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
